import { parseHtmlString } from "@/utils/helpers/html";
import { Heading, Paragraph } from "@/components/ui/Typography"
import { WebsitePageSection } from "@/types"
import EditableSection from "@/components/admin/Content/WebPages/EditableSection";
import InlinePressReleaseEditor from "@/components/admin/Content/WebPages/InlineEditors/InlinePressReleaseEditor";
import { useHomePageHighlights } from "@/lib/api/hooks/press-release";
import { PressRelease as PressReleaseType } from "@/lib/api/types";

// Helper function to format date
const formatPublishedDate = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const PressRelease = ({ data }: { data: WebsitePageSection }) => {
  // Fetch live highlights
  const { data: highlightsData } = useHomePageHighlights();
  const highlightedPressReleases = highlightsData?.data || [];

  return (
    <EditableSection
      sectionTag={data.sectionTag}
      contentType={data.contentType}
      data={data}
      customEditor={InlinePressReleaseEditor}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Paragraph className="bg-gray-50 rounded-2xl px-3 py-1 !text-sm flex items-center justify-center text-forestGreen-100 w-fit">Press Release</Paragraph>
          <div className="flex items-center gap-6">
            <Heading level={4} >{data.title ?? "Press Release Highlights"}</Heading>
          </div>
          <Paragraph dangerouslySetInnerHTML={parseHtmlString(data.description ?? "Display featured press releases on the landing page.")} />
        </div>

        {/* Live Preview of Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {highlightedPressReleases.length > 0 ? (
            highlightedPressReleases.map((pr: PressReleaseType) => (
              <div key={pr.pressReleaseId} className="border border-gray-100 rounded-lg overflow-hidden shadow-sm">
                <div className="h-32 bg-gray-100 relative">
                  <img
                    src={pr.coverImage || "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png"}
                    alt={pr.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      if (e.currentTarget.src !== "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png") {
                        e.currentTarget.src = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png";
                      }
                    }}
                  />
                </div>
                <div className="p-3">
                  <h5 className="font-medium text-sm line-clamp-1">{pr.title}</h5>
                  {(pr.datePublished || pr.createdByUsername) && (
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                      {pr.datePublished && (
                        <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(pr.datePublished)}</p>
                      )}
                      {pr.createdByUsername && (
                        <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {pr.createdByUsername}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-3 text-center py-4 text-gray-400 text-sm border-2 border-dashed border-gray-100 rounded-lg">
              No highlights selected. Click to add.
            </div>
          )}
        </div>
      </div>
    </EditableSection>
  )
}

export default PressRelease